import React, { useEffect, useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Activity, Clock, Loader2, MapPin, AlertTriangle, ChevronRight, Flame, Building2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../contexts/AuthContext';

const DAY_LABELS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

function heatColor(value, max) {
  if (!value || !max) return 'transparent';
  const intensity = Math.min(1, value / max);
  if (intensity < 0.15) return 'rgba(16, 185, 129, 0.10)';
  if (intensity < 0.35) return 'rgba(34, 197, 94, 0.30)';
  if (intensity < 0.55) return 'rgba(234, 179, 8, 0.45)';
  if (intensity < 0.75) return 'rgba(249, 115, 22, 0.60)';
  return 'rgba(220, 38, 38, 0.85)';
}

const HeatmapTab = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [days, setDays] = useState(90);
  const [hotelId, setHotelId] = useState('all');
  const [hotels, setHotels] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data: h } = await api.get('/hotels');
        setHotels(h || []);
      } catch (_) { setHotels([]); }
    })();
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ days });
        if (hotelId !== 'all') params.append('hotel_id', hotelId);
        const { data: d } = await api.get(`/analytics/temporal-heatmap?${params}`);
        setData(d);
      } catch (e) {
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [days, hotelId]);

  const maxValue = useMemo(() => {
    if (!data?.matrix) return 0;
    let m = 0;
    data.matrix.forEach(row => row.forEach(v => { if (v > m) m = v; }));
    return m;
  }, [data]);

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }
  if (!data || data.total_tickets === 0) {
    return (
      <div className="text-center py-12 text-slate-500">
        <Activity className="w-10 h-10 text-slate-300 mx-auto mb-2" />
        <p className="text-sm">Nessun dato sufficiente nella finestra selezionata.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="heatmap-temporal-tab">
      <div className="flex flex-wrap items-center gap-2">
        {user?.role === 'super_admin' && (
          <Select value={hotelId} onValueChange={setHotelId}>
            <SelectTrigger className="w-[200px] h-9" data-testid="heatmap-hotel-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli hotel</SelectItem>
              {hotels.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v))}>
          <SelectTrigger className="w-[160px] h-9" data-testid="heatmap-days-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="30">Ultimi 30 giorni</SelectItem>
            <SelectItem value="90">Ultimi 90 giorni</SelectItem>
            <SelectItem value="180">Ultimi 6 mesi</SelectItem>
            <SelectItem value="365">Ultimo anno</SelectItem>
          </SelectContent>
        </Select>
        <Badge variant="secondary" className="text-xs">{data.total_tickets} ticket analizzati</Badge>
        {data.peak_cell && (
          <Badge className="bg-red-600 text-white text-xs flex items-center gap-1">
            <Flame className="w-3 h-3" /> Picco: {data.peak_cell.day} ore {String(data.peak_cell.hour).padStart(2, '0')}:00 ({data.peak_cell.count})
          </Badge>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200" data-testid="heatmap-matrix">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50">
              <th className="text-left p-2 font-semibold text-slate-600 sticky left-0 bg-slate-50">Giorno</th>
              {Array.from({ length: 24 }, (_, h) => (
                <th key={h} className="px-1.5 py-2 text-center font-mono text-[10px] text-slate-500">{String(h).padStart(2, '0')}</th>
              ))}
              <th className="px-2 py-2 text-center font-semibold text-slate-600">Tot</th>
            </tr>
          </thead>
          <tbody>
            {data.matrix.map((row, dIdx) => {
              const rowTotal = row.reduce((a, b) => a + b, 0);
              return (
                <tr key={dIdx} className="border-t border-slate-100">
                  <td className="px-2 py-1 font-bold text-slate-700 sticky left-0 bg-white" data-testid={`heatmap-row-${DAY_LABELS[dIdx]}`}>{DAY_LABELS[dIdx]}</td>
                  {row.map((val, hIdx) => (
                    <td
                      key={hIdx}
                      className="text-center font-mono text-[10px] font-semibold"
                      style={{
                        background: heatColor(val, maxValue),
                        color: val === 0 ? '#94a3b8' : (val / maxValue > 0.5 ? 'white' : '#334155'),
                        minWidth: '24px',
                        height: '28px',
                      }}
                      title={`${DAY_LABELS[dIdx]} ${String(hIdx).padStart(2, '0')}:00 — ${val} ticket`}
                    >
                      {val > 0 ? val : '·'}
                    </td>
                  ))}
                  <td className="px-2 py-1 text-center font-bold text-slate-900">{rowTotal}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-slate-200">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-bold">Distribuzione per Giorno</CardTitle></CardHeader>
          <CardContent className="space-y-1.5">
            {data.by_day.sort((a, b) => b.count - a.count).map(d => (
              <div key={d.day} className="flex items-center gap-2 text-xs">
                <span className="w-10 font-bold text-slate-700">{d.day}</span>
                <div className="flex-1 bg-slate-100 rounded-full h-3 overflow-hidden">
                  <div className="h-full bg-blue-500" style={{ width: `${d.pct}%` }} />
                </div>
                <span className="w-12 text-right font-mono text-slate-600">{d.count}</span>
                <span className="w-12 text-right font-mono text-slate-400">{d.pct}%</span>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-bold">Top 5 Fasce Orarie</CardTitle></CardHeader>
          <CardContent className="space-y-1.5">
            {[...data.by_hour].sort((a, b) => b.count - a.count).slice(0, 5).map(h => (
              <div key={h.hour} className="flex items-center gap-2 text-xs">
                <span className="w-12 font-mono font-bold text-slate-700">{String(h.hour).padStart(2, '0')}:00</span>
                <div className="flex-1 bg-slate-100 rounded-full h-3 overflow-hidden">
                  <div className="h-full bg-amber-500" style={{ width: `${h.pct * 4}%` }} />
                </div>
                <span className="w-12 text-right font-mono text-slate-600">{h.count}</span>
                <span className="w-12 text-right font-mono text-slate-400">{h.pct}%</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const GenealogyTab = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [minTickets, setMinTickets] = useState(3);
  const [months, setMonths] = useState(24);
  const [hotelId, setHotelId] = useState('all');
  const [hotels, setHotels] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    (async () => {
      try {
        const { data: h } = await api.get('/hotels');
        setHotels(h || []);
      } catch (_) { setHotels([]); }
    })();
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ min_tickets: minTickets, months });
        if (hotelId !== 'all') params.append('hotel_id', hotelId);
        const { data: d } = await api.get(`/analytics/asset-genealogy-list?${params}`);
        setItems(d.items || []);
      } catch (e) {
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [minTickets, months, hotelId]);

  return (
    <div className="space-y-4" data-testid="genealogy-tab">
      <div className="flex flex-wrap items-center gap-2">
        {user?.role === 'super_admin' && (
          <Select value={hotelId} onValueChange={setHotelId}>
            <SelectTrigger className="w-[200px] h-9" data-testid="genealogy-hotel-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli hotel</SelectItem>
              {hotels.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={String(months)} onValueChange={(v) => setMonths(parseInt(v))}>
          <SelectTrigger className="w-[160px] h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="6">Ultimi 6 mesi</SelectItem>
            <SelectItem value="12">Ultimo anno</SelectItem>
            <SelectItem value="24">Ultimi 2 anni</SelectItem>
            <SelectItem value="36">Ultimi 3 anni</SelectItem>
          </SelectContent>
        </Select>
        <Select value={String(minTickets)} onValueChange={(v) => setMinTickets(parseInt(v))}>
          <SelectTrigger className="w-[160px] h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="3">Min. 3 interventi</SelectItem>
            <SelectItem value="5">Min. 5 interventi</SelectItem>
            <SelectItem value="10">Min. 10 interventi</SelectItem>
          </SelectContent>
        </Select>
        <Badge variant="secondary" className="text-xs">{items.length} aree con concentrazione</Badge>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <MapPin className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="text-sm">Nessuna area con concentrazione di interventi.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="genealogy-list">
          {items.map((it, idx) => {
            const severity = it.count >= 15 ? 'high' : it.count >= 8 ? 'med' : 'low';
            const colorMap = {
              high: { bg: 'rgba(220, 38, 38, 0.10)', border: '#dc2626', text: '#991b1b' },
              med: { bg: 'rgba(245, 158, 11, 0.10)', border: '#f59e0b', text: '#b45309' },
              low: { bg: 'rgba(59, 130, 246, 0.10)', border: '#3b82f6', text: '#1e40af' },
            };
            const c = colorMap[severity];
            return (
              <button
                key={`${it.hotel_id}-${it.area}-${idx}`}
                onClick={() => navigate(`/genealogia/${it.hotel_id}/${encodeURIComponent(it.area)}`)}
                className="text-left p-4 rounded-lg border-2 transition hover:scale-[1.02] hover:shadow-md"
                style={{ background: c.bg, borderColor: c.border }}
                data-testid={`genealogy-card-${idx}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider truncate" style={{ color: c.text }}>
                      <Building2 className="w-3 h-3 shrink-0" /> {it.hotel_name}
                    </div>
                    <div className="text-2xl font-extrabold text-slate-900 mt-1">{it.area}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400 shrink-0 mt-1" />
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-200/60">
                  <div>
                    <div className="text-[10px] uppercase text-slate-500 font-semibold">Interventi</div>
                    <div className="text-xl font-bold" style={{ color: c.text }}>{it.count}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase text-slate-500 font-semibold">Aperti</div>
                    <div className="text-xl font-bold text-slate-900">{it.open}</div>
                  </div>
                </div>
                {severity === 'high' && (
                  <div className="mt-2 flex items-center gap-1 text-[10px] font-bold" style={{ color: '#dc2626' }}>
                    <AlertTriangle className="w-3 h-3" /> Concentrazione anomala
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const IntelCenterPage = () => {
  return (
    <div className="space-y-6" data-testid="intel-center-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 flex items-center gap-2">
          <Activity className="w-7 h-7 text-blue-600" /> Intel & Analytics
        </h1>
        <p className="text-sm text-slate-500 mt-1">Heatmap temporale e genealogia degli asset basate sullo storico ticket.</p>
      </div>

      <Tabs defaultValue="heatmap" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="heatmap" data-testid="tab-heatmap"><Clock className="w-4 h-4 mr-1" /> Heatmap Temporale</TabsTrigger>
          <TabsTrigger value="genealogy" data-testid="tab-genealogy"><MapPin className="w-4 h-4 mr-1" /> Genealogia Asset</TabsTrigger>
        </TabsList>
        <TabsContent value="heatmap" className="mt-6"><HeatmapTab /></TabsContent>
        <TabsContent value="genealogy" className="mt-6"><GenealogyTab /></TabsContent>
      </Tabs>
    </div>
  );
};

export default IntelCenterPage;
